import React, { FC, ChangeEvent, useState, useContext, useEffect } from 'react';
import { LineChartOutlined } from '@ant-design/icons';
import { Text, Grid, customProperties as vars } from '@resi-media/resi-ui';
import { NavTabs, NavTab, InfoBox } from '../../components';
import { BarChartOutlined, DesktopOutlined, FacebookOutlined, LinkOutlined, YoutubeOutlined } from '@ant-design/icons';
import { AnalyticsTitleIcon, AnalyticsTitleWrapper, EventAnalyticsWrapper } from './event-analytics.styles';
import { EmbedPlayerAnalytics } from './EmbedPlayer';
import { StreamAnalytics } from './StreamAnalytics';
import { AllDestinationsAnalytics } from './AllDestinations';
import { YoutubeAnalytics } from './YouTubeAnalytics';
import { FacebookAnalytics } from './FacebookAnalytics';
import { EventAnalyticsContext } from '../../contexts/eventanalytics/event-analytics';
import EventHeaderPill from '../../components/EventHeaderPill/event-header-pill';
import { EVENT_ANALYTICS_CONTEXT_KEYS } from './event-analytics-constants';
import { fetchEventNamesFromEventIdList } from '../../components/ViewEvents/api';
import { MPEventName, MPEventProperty, trackMixpanelEvent } from '../../mixpanel';
import { START_DATE_LOCAL } from './date-range-options';

const PageValues = {
  ALL_DESTINATION: 'all-destination',
  EMBED_PLAYER: 'embed-player',
  STREAM_URL: 'stream-url',
  YOUTUBE: 'youtube',
  FACEBOOK: 'facebook',
};

interface EventAnalyticsViewProps {
  auth?: any;
}

const EventAnalyticsView: FC<EventAnalyticsViewProps> = ({ auth }) => {
  const hasPermissionAllDestinations: boolean = auth.currentUser.hasToggle('historicalAllDestinations');
  const hasPermissionStreamURL: boolean = auth.currentUser.hasToggle('historicalStreamURL');
  const hasPermissionYouTube: boolean = auth.currentUser.hasToggle('historicalYouTube');
  const hasPermissionFacebook: boolean = auth.currentUser.hasToggle('historicalFacebook');
  const [tabValue, setTabValue] = useState<string>('embed-player');
  const [isEventNameLoading, setIsEventNameLoading] = useState<boolean>(false);
  const [isInfoBoxDisplayed, setInfoBoxDisplayStatus] = useState<boolean>(true);
  const [eventHeaderLabelValue, setEventHeaderValue] = useState<string>('');
  const {
    EventAnalyticsState: { eventId, customerId },
    dispatch,
  } = useContext(EventAnalyticsContext);
  const onNavTabChange = (event: ChangeEvent<unknown>, value: string) => {
    setTabValue(value);
    sendViewMixpanelEvent(value);
  };

  const sendViewMixpanelEvent = (value: string) => {
    let analyticsType = '';

    switch (value) {
      case 'all-destination':
        analyticsType = 'All Destinations';
        break;
      case 'embed-player':
        analyticsType = 'Embed Player';
        break;
      case 'stream-url':
        analyticsType = 'Stream URL';
        break;
      case 'youtube':
        analyticsType = 'YouTube';
        break;
      case 'facebook':
        analyticsType = 'Facebook';
        break;
    }

    const mixpanelProps = {
      [MPEventProperty.TRANSCODED_EVENT_UUID]: eventId,
      [MPEventProperty.ANALYTICS_TYPE]: analyticsType,
    };

    if (!eventId) {
      delete mixpanelProps[MPEventProperty.TRANSCODED_EVENT_UUID];
    }

    trackMixpanelEvent(MPEventName.ANALYTICS_VIEW, mixpanelProps);
  };

  const displayPage = (value: string) => {
    switch (value) {
      case PageValues.ALL_DESTINATION:
        return <AllDestinationsAnalytics />;
      case PageValues.EMBED_PLAYER:
        return <EmbedPlayerAnalytics />;
      case PageValues.STREAM_URL:
        return <StreamAnalytics />;
      case PageValues.YOUTUBE:
        return <YoutubeAnalytics />;
      case PageValues.FACEBOOK:
        return <FacebookAnalytics />;
      default:
        return <EmbedPlayerAnalytics />;
    }
  };

  const updateEventNameFromEventID = async (): Promise<void> => {
    setIsEventNameLoading(true);
    const [response] = await fetchEventNamesFromEventIdList([eventId || ''], customerId);
    if (response) {
      const { name: eventName } = response;
      if (eventName) {
        setEventHeaderValue(eventName);
      } else {
        setEventHeaderValue(`Event ID: ${eventId}`);
      }
      setIsEventNameLoading(false);
    } else {
      setEventHeaderValue(`Event ID: ${eventId}`);
      setIsEventNameLoading(false);
    }
  };

  const handleCloseEventHeader = (): void => {
    history.pushState({}, '', `event-analytics`);
    setEventHeaderValue('');
    dispatch({ type: EVENT_ANALYTICS_CONTEXT_KEYS.EVENT_ID, payload: null });
  };

  useEffect(() => {
    if (eventId) {
      updateEventNameFromEventID();
    }

    sendViewMixpanelEvent('embed-player');
  }, [eventId]);

  return (
    <EventAnalyticsWrapper>
      <Grid display="grid" style={{ padding: vars.spacingXl }} data-testid="analytics-page">
        <Grid display="grid" gridAutoColumns="1fr" gridTemplateColumns="repeat(3, auto)">
          <AnalyticsTitleWrapper>
            <AnalyticsTitleIcon data-testid="analytics-icon">
              <LineChartOutlined style={{ fontSize: vars.fontSize24, color: vars.colorAccent800 }} />
            </AnalyticsTitleIcon>
            <Text.Heading type="h4" customCss={`color: ${vars.colorAccent800};`} data-testid="analytics-page-header">
              Event Analytics
            </Text.Heading>
            {eventId ? (
              <EventHeaderPill
                isLoading={isEventNameLoading}
                eventHeaderLabel={eventHeaderLabelValue}
                onClose={handleCloseEventHeader}
              />
            ) : null}
          </AnalyticsTitleWrapper>
        </Grid>
        {isInfoBoxDisplayed && (
          <InfoBox
            closeButton
            infoText={
              `Historical analytics are not available prior to ${START_DATE_LOCAL.toFormat('MM/dd/yy')}. To learn more, see the analytics documentation.`
            }
            onClose={() => setInfoBoxDisplayStatus(false)}
          />
        )}
        <Grid.Item>
          <NavTabs value={tabValue} onChange={onNavTabChange}>
            {hasPermissionAllDestinations && (
              <NavTab value={PageValues.ALL_DESTINATION} icon={<BarChartOutlined />} label="All Destinations" />
            )}
            {/* Default Tab , Hence no permissions required */}
            <NavTab value={PageValues.EMBED_PLAYER} icon={<DesktopOutlined />} label="Embed Player" />
            {hasPermissionStreamURL && (
              <NavTab value={PageValues.STREAM_URL} icon={<LinkOutlined />} label="Stream URL" />
            )}
            {hasPermissionYouTube && <NavTab value={PageValues.YOUTUBE} icon={<YoutubeOutlined />} label="YouTube" />}
            {hasPermissionFacebook && (
              <NavTab value={PageValues.FACEBOOK} icon={<FacebookOutlined />} label="Facebook" />
            )}
          </NavTabs>
        </Grid.Item>
        {displayPage(tabValue)}
      </Grid>
    </EventAnalyticsWrapper>
  );
};

EventAnalyticsView.displayName = 'EventAnalyticsView';

export default EventAnalyticsView;
